import { useCallback } from "react";
import { useGameStateContext } from "../GameStateProvider";
import { useAudioContext } from "../AudioProvider";
import { CONSTANTS } from "../data";
import { isActionAllowedInTutorial } from "../utils/tutorialUtils";
import { calculateQualityGrade, calculateTempScore, calculateTimingScore } from "../utils/qualityUtils";

export const useBrewingActions = () => {
  const { state, dispatch } = useGameStateContext();
  const audio = useAudioContext();

  const handleSelectItem = useCallback(
    (itemId: string) => {
      if (
        !isActionAllowedInTutorial(
          { type: "selectItem", itemId },
          state.tutorialState
        )
      ) {
        return;
      }

      const allItemsMap = new Map();
      Object.entries(CONSTANTS.ITEMS).forEach(([id, item]) => {
        allItemsMap.set(id, { ...item, id });
      });
      Object.entries(CONSTANTS.RECIPES).forEach(([id, recipe]) => {
        allItemsMap.set(recipe.result.id, recipe.result);
      });

      const itemDef = allItemsMap.get(itemId);
      if (!itemDef) return;

      const isAlreadySlotted = state.brewStations.some((s) =>
        s.slots.includes(itemId)
      );
      if (isAlreadySlotted) {
        return;
      }

      const itemType = itemDef.type;

      // Allow ingredients AND drinks to go in ingredient slots (slot 0)
      if (CONSTANTS.INVENTORY_CATEGORIES.INGREDIENTS.types.includes(itemType) || 
          CONSTANTS.INVENTORY_CATEGORIES.DRINKS.types.includes(itemType)) {
        for (
          let stationIndex = 0;
          stationIndex < state.brewStations.length;
          stationIndex++
        ) {
          if (state.brewStations[stationIndex].slots[0] === null) {
            dispatch({
              type: "ADD_TO_STATION",
              stationIndex,
              slotIndex: 0,
              item: itemId,
            });
            dispatch({ type: "SET_INVENTORY_TAB", tab: "tools" });
            audio.playSfx("click");
            return;
          }
        }
      }
      
      // Allow tools, drinks, AND ingredients to go in tool slots (slot 1)
      if (CONSTANTS.INVENTORY_CATEGORIES.TOOLS.types.includes(itemType) || 
          CONSTANTS.INVENTORY_CATEGORIES.DRINKS.types.includes(itemType) ||
          CONSTANTS.INVENTORY_CATEGORIES.INGREDIENTS.types.includes(itemType)) {
        for (
          let stationIndex = 0;
          stationIndex < state.brewStations.length;
          stationIndex++
        ) {
          if (state.brewStations[stationIndex].slots[1] === null) {
            dispatch({
              type: "ADD_TO_STATION",
              stationIndex,
              slotIndex: 1,
              item: itemId,
            });
            audio.playSfx("click");
            return;
          }
        }
      }
    },
    [dispatch, state.brewStations, state.tutorialState, audio]
  );

  const handleRemoveSlotItem = useCallback(
    (stationIndex: number, slotIndex: number) => {
      dispatch({ type: "REMOVE_FROM_STATION", stationIndex, slotIndex });
    },
    [dispatch]
  );

  const handleAttemptBrew = useCallback(
    (stationIndex: number) => {
      const station = state.brewStations[stationIndex];
      if (!station.slots[0] || !station.slots[1]) return;

      const ingredients = [station.slots[0], station.slots[1]].sort();

      if (
        !isActionAllowedInTutorial(
          { type: "brew", ingredients },
          state.tutorialState
        )
      ) {
        return;
      }

      const quantity = station.quantity;
      let canBrew = true;
      const allItemsMap = new Map();
      Object.entries(CONSTANTS.ITEMS).forEach(([id, item]) => {
        allItemsMap.set(id, { ...item, id });
      });
      Object.entries(CONSTANTS.RECIPES).forEach(([id, recipe]) => {
        allItemsMap.set(recipe.result.id, recipe.result);
      });

      [station.slots[0], station.slots[1]].forEach((itemId) => {
        if (!itemId) return;
        const itemDef = allItemsMap.get(itemId);
        if (itemDef?.consumable) {
          if ((state.inventory[itemId] || 0) < quantity) {
            canBrew = false;
          }
        }
      });

      if (!canBrew) {
        dispatch({ type: "SET_HUD_HINT", hint: "Not enough ingredients!" });
        setTimeout(() => dispatch({ type: "CLEAR_HUD_HINT" }), 2000);
        return;
      }

      const recipeKey = ingredients.join("_");

      // Create normalized recipes map
      const normalizedRecipes = new Map();
      Object.entries(CONSTANTS.RECIPES).forEach(([id, recipe]) => {
        const key = recipe.ingredients.sort().join("_");
        normalizedRecipes.set(key, { id, recipe });
      });

      const recipe = normalizedRecipes.get(recipeKey);

      // Always clear the station after an attempt
      dispatch({ type: "REMOVE_FROM_STATION", stationIndex, slotIndex: 0 });
      dispatch({ type: "REMOVE_FROM_STATION", stationIndex, slotIndex: 1 });
      dispatch({ type: "SET_BREW_QUANTITY", stationIndex, quantity: 1 });

      if (recipe) {
        let qualityMultiplier;
        if (state.qualityGrades.enabled) {
          let tempScore = 0;
          let timingScore = 0;
          const techniqueScore = CONSTANTS.QUALITY_SCORING.maxTechniqueScore * 0.8; // Base technique

          const tempRange = CONSTANTS.TEMPERATURE_CONTROL.optimalRanges[recipe.recipe.result.id];
          if (tempRange) {
            tempScore = calculateTempScore(state.temperatureControl.currentTemp, tempRange);
          } else {
            tempScore = CONSTANTS.QUALITY_SCORING.maxTempScore * 0.5; // Default score if no range
          }

          const timingInfo = CONSTANTS.TIMING_WINDOWS[recipe.recipe.result.id];
          if (timingInfo && state.lastCreationTimestamps) {
            const requiredIngredientTimestamp = state.lastCreationTimestamps[timingInfo.required];
            if (requiredIngredientTimestamp) {
              const elapsedTime = Date.now() - requiredIngredientTimestamp;
              timingScore = calculateTimingScore(elapsedTime, timingInfo.window);
            }
          } else {
            timingScore = CONSTANTS.QUALITY_SCORING.maxTimingScore * 0.5; // Default score if no timing
          }

          const totalScore = tempScore + timingScore + techniqueScore;
          const qualityGrade = calculateQualityGrade(totalScore);
          qualityMultiplier = qualityGrade.multiplier;
          dispatch({ type: "SET_QUALITY_GRADE", grade: qualityGrade });
        }

        // Add result to inventory
        dispatch({
          type: "BREW_SUCCESS",
          resultItem: recipe.recipe.result,
          consumedItems: ingredients,
          quantity: quantity,
          qualityMultiplier,
        });

        // Discover recipe if not already discovered
        if (!state.discoveredRecipes.includes(recipe.id)) {
          dispatch({ type: "DISCOVER_RECIPE", recipeId: recipe.id });
          audio.playSfx("discovery");
        } else {
          audio.playSfx("brew");
        }
      } else {
        // Failed brew
        audio.playSfx("click"); // Or a dedicated fail sound
      }
    },
    [
      state.brewStations,
      state.discoveredRecipes,
      state.tutorialState,
      state.inventory,
      state.qualityGrades.enabled,
      state.temperatureControl.currentTemp,
      state.lastCreationTimestamps,
      dispatch,
      audio,
    ]
  );

  const handleTempChange = useCallback(
    (temp: number) => {
      dispatch({ type: "SET_TEMPERATURE", temperature: temp });
    },
    [dispatch]
  );

  const handleQuantityChange = useCallback(
    (stationIndex: number, newQuantity: number) => {
      dispatch({
        type: "SET_BREW_QUANTITY",
        stationIndex,
        quantity: newQuantity,
      });
    },
    [dispatch]
  );

  return {
    handleSelectItem,
    handleRemoveSlotItem,
    handleAttemptBrew,
    handleTempChange,
    handleQuantityChange,
  };
};
